import { Container, Graphics, Text } from "pixi.js";
import type { MapSchema } from "@colyseus/schema";

import type { Ship } from "../../../shared/ArenaState";

/** How many leaders are listed. Five is what fits before the panel starts covering the sea. */
const ROWS = 5;

const WIDTH = 220;
const PADDING = 10;
const TITLE_HEIGHT = 26;
const ROW_HEIGHT = 21;
/** Space taken by the rule between the leaders and the player's own line. */
const DIVIDER_GAP = 10;
const HEIGHT = PADDING * 2 + TITLE_HEIGHT + ROWS * ROW_HEIGHT + DIVIDER_GAP + ROW_HEIGHT;

/** Gap from the screen edge, and from the toolbar above it. Matches the minimap. */
const MARGIN = 16;
/** Height of the DOM toolbar the panel has to clear — see design/UI.md. */
const TOOLBAR_HEIGHT = 56;
const CORNER_RADIUS = 8;

const PANEL_COLOR = 0x071a21;
const PANEL_ALPHA = 0.55;
const BORDER_COLOR = 0xf2e2c4;
const BORDER_ALPHA = 0.35;
const BORDER_WIDTH = 2;

/** Cream, as everywhere else. */
const TEXT_COLOR = 0xf2e2c4;
/** You. The action red, same as the minimap dot, so the two panels agree about who you are. */
const PLAYER_COLOR = 0xc8452f;
/**
 * A ghost ship's row — the same pale blue its hull is washed with, so a name on the board and a
 * derelict in the water are recognisably one thing. See design/Battle.md.
 *
 * It marks the row rather than removing it: a rich hull whose captain has gone is the best target
 * in the arena, and the board is where players go looking for one. What the colour adds is that
 * it is *free* — the prize is still there and nothing is defending it.
 */
const GHOST_COLOR = 0x8fb0bd;
const TITLE_SIZE = 15;
const ROW_SIZE = 14;

/**
 * How often the board is rebuilt, in Hz. Ranks do not need a frame's worth of precision,
 * and re-laying out a dozen `Text` objects sixty times a second is real work for a panel
 * nobody can read that fast.
 */
const REFRESH_HZ = 5;

/** Longest name drawn before it is cut, in characters. Keeps the score column clear. */
const NAME_MAX_DISPLAY = 12;

/** One line of the board: a rank-and-name label on the left, a score on the right. */
interface Row {
    label: Text;
    score: Text;
}

/**
 * The leaderboard in the top-right, under the toolbar: the top few ships by score, then a
 * rule, then the player's own line with their rank.
 *
 * The player's line is always shown, even when they are already in the list above. A
 * player in 27th place needs to know both that they are 27th and what 5th place costs, and
 * a board that only shows the top five tells a beginner nothing about themselves.
 *
 * Pixi rather than DOM: it is live score data that changes throughout a fight, and it
 * belongs on the same clock as the sea. See design/UI.md and design/Battle.md.
 */
export class Leaderboard {
    private readonly _container = new Container();
    private readonly _rows: Row[] = [];
    private readonly _player: Row;

    private _sinceRefresh = Number.POSITIVE_INFINITY;

    public get container(): Container {
        return this._container;
    }

    constructor() {
        // The panel never changes, so it is drawn once and left alone.
        const panel = new Graphics().roundRect(0, 0, WIDTH, HEIGHT, CORNER_RADIUS);
        panel.fill({ color: PANEL_COLOR, alpha: PANEL_ALPHA });
        panel.stroke({ color: BORDER_COLOR, alpha: BORDER_ALPHA, width: BORDER_WIDTH });

        const title = new Text({
            text: "Leaderboard",
            x: PADDING,
            y: PADDING,
            style: { fontFamily: "Grobold", fontSize: TITLE_SIZE, fill: TEXT_COLOR }
        });

        const dividerY = PADDING + TITLE_HEIGHT + ROWS * ROW_HEIGHT + DIVIDER_GAP / 2;
        const divider = new Graphics()
            .moveTo(PADDING, dividerY)
            .lineTo(WIDTH - PADDING, dividerY)
            .stroke({ color: BORDER_COLOR, alpha: BORDER_ALPHA, width: 1 });

        // Vertically it is under the toolbar and stays there; the x is the viewport's
        // business, so `resize` owns it and lands it on the first frame.
        this._container.y = TOOLBAR_HEIGHT + MARGIN;
        this._container.addChild(panel, title, divider);

        for (let i = 0; i < ROWS; i++) {
            this._rows.push(this._addRow(PADDING + TITLE_HEIGHT + i * ROW_HEIGHT));
        }
        this._player = this._addRow(dividerY + DIVIDER_GAP / 2);
    }

    /** Re-ranks and redraws, at REFRESH_HZ rather than per frame. */
    public update(ships: MapSchema<Ship> | null, localSessionId: string, deltaSeconds: number): void {
        this._sinceRefresh += deltaSeconds;
        if (this._sinceRefresh < 1 / REFRESH_HZ) return;
        this._sinceRefresh = 0;

        const ranked: { sessionId: string; ship: Ship }[] = [];
        // Ghosts are ranked with everyone else. The board is a **bounty list** before it is a
        // ranking of captains (design/Battle.md), and a leader who quits is the biggest bounty in
        // the sea: a hull nobody is steering, holding a top score, that cannot shoot back. Taking
        // it off the board would hide the best target in the arena at the moment it becomes the
        // best target. It is drawn in GHOST_COLOR instead, so the row says which it is.
        ships?.forEach((ship, sessionId) => ranked.push({ sessionId, ship }));

        // Ties broken by session id so two ships on the same score do not swap places
        // every refresh — a board that shuffles under a still fight is unreadable.
        ranked.sort((a, b) => b.ship.score - a.ship.score || a.sessionId.localeCompare(b.sessionId));

        for (let i = 0; i < ROWS; i++) {
            const entry = ranked[i];
            this._setRow(this._rows[i], i + 1, entry?.ship, entry?.sessionId === localSessionId);
        }

        // Always drawn, even when the player is already in the five above: someone in 27th
        // needs to know both that they are 27th and what fifth place costs.
        const rank = ranked.findIndex((entry) => entry.sessionId === localSessionId);
        this._setRow(this._player, rank + 1, rank >= 0 ? ranked[rank].ship : undefined, true);
    }

    /** Right-anchored, so the board rides the viewport's right edge the way the map does. */
    public resize(width: number, _height: number): void {
        this._container.x = width - WIDTH - MARGIN;
    }

    public destroy(): void {
        this._container.destroy({ children: true });
    }

    private _addRow(y: number): Row {
        const style = { fontFamily: "Grobold", fontSize: ROW_SIZE, fill: TEXT_COLOR };

        const label = new Text({ text: "", x: PADDING, y, style });
        // Right-anchored, so the score column stays put whatever the number does.
        const score = new Text({ text: "", x: WIDTH - PADDING, y, anchor: { x: 1, y: 0 }, style });

        this._container.addChild(label, score);
        return { label, score };
    }

    /**
     * Fills a line, or empties it when there is no ship for it. Assigned unconditionally:
     * both Pixi setters return early when the value has not changed, so an unchanged row
     * costs a comparison and no glyph work.
     *
     * `isLocal` wins over the ghost colour, and the case never comes up: a captain who has
     * dropped out is not watching this panel. It is ordered that way because "which row is mine"
     * is the question the board has to answer first, whatever else is true of the ship.
     */
    private _setRow(row: Row, rank: number, ship: Ship | undefined, isLocal: boolean): void {
        const color = isLocal ? PLAYER_COLOR : ship?.ghost ? GHOST_COLOR : TEXT_COLOR;

        row.label.text = ship ? `${rank}. ${trimName(ship.name)}` : "";
        row.score.text = ship ? String(ship.score) : "";
        row.label.style.fill = color;
        row.score.style.fill = color;
    }
}

/** Names are 16 characters at the server, which is wider than the name column. */
function trimName(name: string): string {
    return name.length > NAME_MAX_DISPLAY ? `${name.slice(0, NAME_MAX_DISPLAY - 1)}…` : name;
}
